import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

function TeamCell({ team }) {
    if (!team) return <span className="text-t5c6370">—</span>;
    return (
        <Link to={`/squadre/${team.id}`} className="flex items-center gap-2 hover:text-t2bb6ff transition-colors min-w-0">
            {team.logo_url && <img src={team.logo_url} alt="" className="w-5 h-5 object-contain shrink-0" />}
            <span className="truncate">{team.name}</span>
        </Link>
    );
}

function CompetitionPanel({ comp }) {
    const suspended = comp.suspended || [];
    const warnings = comp.warnings || [];
    const all = useMemo(() => {
        const rows = (comp.all || []).map((p) => ({
            ...p,
            red_total: (p.red_direct_total || 0) + (p.red_double_total || 0),
        }));
        rows.sort((a, b) =>
            b.red_total - a.red_total ||
            (b.yellow_total || 0) - (a.yellow_total || 0) ||
            (a.name || "").localeCompare(b.name || ""),
        );
        return rows;
    }, [comp]);

    return (
        <div className="space-y-6" data-testid={`disc-panel-${comp.code || comp.slug}`}>
            {/* Squalificati */}
            <section className="op-card overflow-hidden">
                <div className="px-5 py-3 border-b border-t22252a bg-t0e1014 flex items-center gap-2">
                    <span className="text-lg">🟥</span>
                    <div className="op-label text-[11px] text-tff4d4d">SQUALIFICATI · saltano la prossima</div>
                    <span className="op-mono text-xs text-t8f95a3 ml-auto">{suspended.length}</span>
                </div>
                {suspended.length === 0 ? (
                    <div className="px-5 py-4 op-mono text-xs text-t5c6370">Nessun giocatore squalificato.</div>
                ) : (
                    <ul className="divide-y divide-t16181c">
                        {suspended.map((p) => (
                            <li key={p.pes_id} className="px-5 py-3 flex items-center gap-3 text-sm" data-testid={`disc-susp-${p.pes_id}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="text-tffffff font-semibold truncate">{p.name || "—"}</div>
                                    <div className="op-mono text-[11px] text-tff7a1a">{p.suspension_reason || "squalificato"}</div>
                                </div>
                                <div className="w-44 hidden sm:block op-mono text-xs"><TeamCell team={p.team} /></div>
                                <span className="op-label text-[10px] px-2 py-1 border border-tff4d4d text-tff4d4d shrink-0">
                                    {p.suspended_remaining || 1} giorn.
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* Diffidati */}
            <section className="op-card overflow-hidden">
                <div className="px-5 py-3 border-b border-t22252a bg-t0e1014 flex items-center gap-2">
                    <span className="text-lg">🟨</span>
                    <div className="op-label text-[11px] text-te8ff00">DIFFIDATI · 1 ammonizione alla squalifica</div>
                    <span className="op-mono text-xs text-t8f95a3 ml-auto">{warnings.length}</span>
                </div>
                {warnings.length === 0 ? (
                    <div className="px-5 py-4 op-mono text-xs text-t5c6370">Nessun diffidato.</div>
                ) : (
                    <ul className="divide-y divide-t16181c">
                        {warnings.map((p) => (
                            <li key={p.pes_id} className="px-5 py-3 flex items-center gap-3 text-sm" data-testid={`disc-warn-${p.pes_id}`}>
                                <div className="flex-1 min-w-0 text-tffffff truncate">{p.name || "—"}</div>
                                <div className="w-44 hidden sm:block op-mono text-xs"><TeamCell team={p.team} /></div>
                                <span className="op-label text-[10px] px-2 py-1 border border-te8ff00 text-te8ff00 shrink-0">
                                    diffidato
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* Classifica ammoniti / espulsi */}
            <section className="op-card overflow-hidden">
                <div className="px-5 py-3 border-b border-t22252a bg-t0e1014 flex items-center gap-2">
                    <span className="text-lg">📋</span>
                    <div className="op-label text-[11px] text-tff7a1a">CLASSIFICA AMMONITI / ESPULSI</div>
                </div>
                {all.length === 0 ? (
                    <div className="px-5 py-4 op-mono text-xs text-t5c6370">Nessuna sanzione registrata.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="disc-table">
                            <thead>
                                <tr className="op-label text-[10px] text-t8f95a3 border-b border-t22252a">
                                    <th className="text-left px-5 py-2 font-normal">Giocatore</th>
                                    <th className="text-left px-3 py-2 font-normal hidden sm:table-cell">Squadra</th>
                                    <th className="text-center px-3 py-2 font-normal">🟨</th>
                                    <th className="text-center px-3 py-2 font-normal">🟥</th>
                                    <th className="text-right px-5 py-2 font-normal">Stato</th>
                                </tr>
                            </thead>
                            <tbody>
                                {all.map((p) => (
                                    <tr key={p.pes_id} className="border-b border-t16181c hover:bg-t16181c/50" data-testid={`disc-row-${p.pes_id}`}>
                                        <td className="px-5 py-2.5 text-tffffff">{p.name || "—"}</td>
                                        <td className="px-3 py-2.5 hidden sm:table-cell op-mono text-xs text-tc9ccd3"><TeamCell team={p.team} /></td>
                                        <td className="px-3 py-2.5 text-center op-mono text-te8ff00">{p.yellow_total || 0}</td>
                                        <td className="px-3 py-2.5 text-center op-mono text-tff4d4d">{p.red_total || 0}</td>
                                        <td className="px-5 py-2.5 text-right">
                                            {p.suspended_for_next ? (
                                                <span className="op-label text-[9px] text-tff4d4d">SQUALIFICATO</span>
                                            ) : p.yellows >= 1 ? (
                                                <span className="op-label text-[9px] text-te8ff00">DIFFIDATO</span>
                                            ) : (
                                                <span className="op-mono text-[10px] text-t5c6370">ok</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}

export default function Disciplinare() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);

    useEffect(() => {
        api.get("/disciplinary/overview")
            .then(({ data: d }) => {
                setData(d);
                // Default: prima competizione con almeno un dato disciplinare
                const comps = d.competitions || [];
                const firstWithData = comps.find((c) => (c.all || []).length > 0) || comps[0];
                if (firstWithData) setActive(firstWithData.code || firstWithData.slug);
            })
            .catch(() => setData({ competitions: [] }))
            .finally(() => setLoading(false));
    }, []);

    const comps = data?.competitions || [];
    const activeComp = comps.find((c) => (c.code || c.slug) === active);

    return (
        <div className="op-page max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="disciplinare-page">
            <div className="op-label text-tff7a1a mb-2">// FAIR PLAY</div>
            <h1 className="op-heading text-4xl sm:text-5xl mb-2">Disciplinare</h1>
            <p className="text-sm text-t8f95a3 mb-6">
                Squalificati, diffidati e classifica di ammoniti ed espulsi per ogni competizione.
                Le sanzioni si scontano nella stessa competizione in cui sono state rimediate.
            </p>

            {loading ? (
                <div className="op-card p-8 op-label text-t8f95a3 animate-pulse">// CARICAMENTO…</div>
            ) : comps.length === 0 ? (
                <div className="op-card p-8 op-mono text-sm text-t8f95a3">Nessuna competizione disponibile.</div>
            ) : (
                <>
                    {/* Tabs competizioni */}
                    <div className="flex flex-wrap gap-2 mb-6" data-testid="disc-comp-tabs">
                        {comps.map((c) => {
                            const key = c.code || c.slug;
                            const count = (c.suspended || []).length + (c.warnings || []).length;
                            const isActive = key === active;
                            return (
                                <button
                                    key={key}
                                    onClick={() => setActive(key)}
                                    className={`op-label text-[11px] px-3 py-2 border transition-colors ${
                                        isActive ? "text-black" : "text-tc9ccd3 border-t22252a hover:border-t3a3f47"
                                    }`}
                                    style={isActive ? { backgroundColor: c.accent, borderColor: c.accent } : {}}
                                    data-testid={`disc-tab-${key}`}
                                >
                                    {c.competition}
                                    {count > 0 && (
                                        <span className={`ml-2 ${isActive ? "opacity-80" : "text-tff7a1a"}`}>{count}</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {activeComp ? (
                        <CompetitionPanel comp={activeComp} />
                    ) : (
                        <div className="op-card p-8 op-mono text-sm text-t8f95a3">Seleziona una competizione.</div>
                    )}
                </>
            )}
        </div>
    );
}
